/**
 * AutopayFunnelTile — Iter-165 · Phase 3 + Iter-166 · Phase 4 +
 * Iter-167 · Phase 1 + Iter-168 · Phases 1 & 4.
 *
 * Finance-only tile in Billing & A/R:
 *   - Snapshot metrics (Clicks / Enrollments / Conversion / Breakdown).
 *   - Trailing window filter (7 / 30 / 90 / All).
 *   - Inline Recharts trend chart with 3 series: CTA Clicks (blue) +
 *     Enrollments (gold) on the left axis, Conversion % (green) on the
 *     right axis.
 *   - One-click CSV export of the currently filtered weekly buckets.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Zap, TrendingUp, Download } from 'lucide-react';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis,
         Tooltip, CartesianGrid, Legend } from 'recharts';

const API = process.env.REACT_APP_BACKEND_URL;

const WINDOWS = [
  { key: '7',   label: '7 Days'  },
  { key: '30',  label: '30 Days' },
  { key: '90',  label: '90 Days' },
  { key: 'all', label: 'All Time' },
];

function toCsv(rows) {
  const header = ['week_start', 'cta_clicks', 'enrollments', 'conversion_rate_pct'];
  const escape = (v) => {
    const s = v == null ? '' : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = [header.join(',')];
  for (const r of rows) {
    lines.push([
      escape(r.week_start),
      escape(r.cta_clicks ?? 0),
      escape(r.enrollments ?? 0),
      escape(r.conversion_rate_pct != null ? r.conversion_rate_pct : (r.conversion_rate ?? 0)),
    ].join(','));
  }
  return lines.join('\n');
}

export default function AutopayFunnelTile() {
  const [d, setD] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [windowKey, setWindowKey] = useState('all');
  const load = useCallback(async () => {
    try {
      const qs = windowKey === 'all' ? '' : `?days=${encodeURIComponent(windowKey)}`;
      const [snap, tl] = await Promise.all([
        fetch(`${API}/api/analytics/autopay-funnel${qs}`, { credentials: 'include' })
          .then((r) => r.ok ? r.json() : null),
        fetch(`${API}/api/analytics/autopay-funnel/timeline${qs}`, { credentials: 'include' })
          .then((r) => r.ok ? r.json() : null),
      ]);
      if (snap) setD(snap);
      if (tl && Array.isArray(tl.timeline)) setTimeline(tl.timeline);
    } catch (_) {}
  }, [windowKey]);
  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, [load]);

  const chartData = useMemo(() => timeline.map((r) => ({
    ...r,
    conversion_rate_pct: r.conversion_rate_pct != null ? r.conversion_rate_pct : (r.conversion_rate || 0),
  })), [timeline]);

  const handleExport = useCallback(() => {
    if (!timeline.length) return;
    const csv = toCsv(timeline);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const stamp = new Date().toISOString().slice(0, 10);
    a.href = url;
    a.download = `necs-autopay-funnel-${windowKey}-${stamp}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 250);
  }, [timeline, windowKey]);

  if (!d) return null;
  return (
    <Card className="p-4 bg-[#141414] border-zinc-800" data-testid="autopay-funnel-tile">
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <Zap className="h-4 w-4 text-blue-400" />
        <p className="text-[10px] uppercase tracking-widest text-zinc-300">Autopay Enrollment Funnel</p>
        <div className="ml-auto flex items-center gap-1"
             data-testid="autopay-funnel-window-selector">
          {WINDOWS.map((w) => {
            const active = windowKey === w.key;
            return (
              <button
                key={w.key}
                type="button"
                onClick={() => setWindowKey(w.key)}
                className="px-2 py-0.5 rounded text-[9px] font-bold uppercase tracking-widest transition-colors"
                style={{
                  backgroundColor: active ? '#C9A962' : '#27272a',
                  color: active ? '#111' : '#e5e5e5',
                }}
                data-testid={`autopay-funnel-window-${w.key}`}
              >
                {w.label}
              </button>
            );
          })}
          {/* Iter-168 · Phase 4 — CSV export of the currently filtered
              weekly buckets. Disabled when the timeline is empty so
              Finance never downloads an empty file. */}
          <Button
            type="button"
            onClick={handleExport}
            disabled={!timeline.length}
            className="ml-1 h-6 px-2 text-[9px] font-bold uppercase tracking-widest"
            style={{ backgroundColor: '#27272a', color: '#e5e5e5' }}
            data-testid="autopay-funnel-export-csv"
            title="Export weekly buckets to CSV"
          >
            <Download className="h-3 w-3 mr-1" /> CSV
          </Button>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div>
          <p className="text-[10px] uppercase tracking-widest text-zinc-500">CTA Clicks</p>
          <p className="text-2xl font-bold text-white" data-testid="funnel-clicks">{d.clicks}</p>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-widest text-zinc-500">Enrollments</p>
          <p className="text-2xl font-bold text-white" data-testid="funnel-enrollments">{d.enrollments}</p>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-widest text-zinc-500">Conversion</p>
          <p className="text-2xl font-bold" style={{ color: '#C9A962' }} data-testid="funnel-conversion">
            <TrendingUp className="inline h-4 w-4 mr-0.5" />
            {d.conversion_rate_pct}%
          </p>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-widest text-zinc-500">ACH / Card</p>
          <p className="text-2xl font-bold text-white" data-testid="funnel-breakdown">
            {d.breakdown?.ach || 0}<span className="text-zinc-500 text-lg mx-1">/</span>{d.breakdown?.card || 0}
          </p>
        </div>
      </div>

      <div className="mt-4" data-testid="autopay-funnel-timeline">
        <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">
          Weekly Conversion Trend
        </p>
        {chartData.length === 0 ? (
          <p className="text-[11px] italic text-zinc-500" data-testid="autopay-funnel-timeline-empty">
            No autopay activity yet in this window.
          </p>
        ) : (
          <div style={{ width: '100%', height: 200 }}>
            <ResponsiveContainer>
              <LineChart data={chartData} margin={{ top: 6, right: 12, left: -12, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                <XAxis dataKey="week_start" tick={{ fill: '#a1a1aa', fontSize: 10 }} tickMargin={4}
                       tickFormatter={(v) => (v || '').slice(5)} />
                {/* Left axis — raw counts. */}
                <YAxis yAxisId="left" tick={{ fill: '#a1a1aa', fontSize: 10 }} allowDecimals={false} />
                {/* Iter-168 · Phase 1 — right axis for conversion %. */}
                <YAxis yAxisId="right" orientation="right" domain={[0, 100]}
                       tick={{ fill: '#a1a1aa', fontSize: 10 }}
                       tickFormatter={(v) => `${v}%`} />
                <Tooltip
                  contentStyle={{ backgroundColor: '#0b0b0b', border: '1px solid #3f3f46', fontSize: 11 }}
                  labelStyle={{ color: '#e5e5e5' }} itemStyle={{ color: '#e5e5e5' }}
                  formatter={(value, name) => (name === 'Conversion %' ? [`${value}%`, name] : [value, name])} />
                <Legend wrapperStyle={{ fontSize: 10, color: '#a1a1aa' }} />
                <Line yAxisId="left" type="monotone" dataKey="cta_clicks" stroke="#3b82f6"
                      strokeWidth={2} dot={{ r: 2 }} name="CTA Clicks" />
                <Line yAxisId="left" type="monotone" dataKey="enrollments" stroke="#C9A962"
                      strokeWidth={2} dot={{ r: 2 }} name="Enrollments" />
                <Line yAxisId="right" type="monotone" dataKey="conversion_rate_pct" stroke="#10b981"
                      strokeWidth={2} strokeDasharray="4 4" dot={{ r: 2 }} name="Conversion %" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </Card>
  );
}
